import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import wordmark from "../../../assets/images/Zephyr.png";
import heroImg from "../../../assets/images/docsheader.png";
import { docsPages, docsSections, slugify } from "./content";
import {
  DocsWrapper,
  TopBar,
  Brand,
  TopActions,
  BackLink,
  MenuToggle,
  Layout,
  Sidebar,
  Backdrop,
  GroupTitle,
  NavItem,
  Main,
  Hero,
  Breadcrumb,
  PageTitle,
  Subtitle,
  Content,
  TableWrap,
  Note,
  Flow,
  StatusPill,
  PagerRow,
  PagerLink,
  Toc,
  DocsFooter,
} from "./styles";

const statusLabels = ["Available", "Coming Soon"];

const renderBlock = (block, i) => {
  switch (block.type) {
    case "h2":
      return (
        <h2 key={i} id={slugify(block.text)}>
          {block.text}
        </h2>
      );
    case "h3":
      return <h3 key={i}>{block.text}</h3>;
    case "list":
      return (
        <ul key={i}>
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );
    case "table":
      return (
        <TableWrap key={i}>
          <table>
            <thead>
              <tr>
                {block.head.map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row) => (
                <tr key={row[0]}>
                  {row.map((cell, c) => (
                    <td key={c}>
                      {statusLabels.includes(cell) ? (
                        <StatusPill available={cell === "Available"}>{cell}</StatusPill>
                      ) : (
                        cell
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      );
    case "flow":
      return (
        <Flow key={i} vertical={block.vertical}>
          <div className="steps">
            {block.steps.map((step, s) => (
              <React.Fragment key={step.title}>
                {s > 0 && <div className="arrow">➜</div>}
                <div className="step">
                  <strong>{step.title}</strong>
                  {step.text && <small>{step.text}</small>}
                </div>
              </React.Fragment>
            ))}
          </div>
          {block.caption && <figcaption>{block.caption}</figcaption>}
        </Flow>
      );
    case "note":
      return <Note key={i}>{block.text}</Note>;
    default:
      return <p key={i}>{block.text}</p>;
  }
};

const DocsPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const index = slug ? docsPages.findIndex((p) => p.slug === slug) : 0;
  const page = docsPages[index];

  useEffect(() => {
    window.scrollTo(0, 0);
    setMenuOpen(false);
    if (page) document.title = `${page.title} | Zephyr Docs`;
  }, [slug, page]);

  if (!page) return <Navigate to="/docs" replace />;

  const prev = docsPages[index - 1];
  const next = docsPages[index + 1];
  const headings = page.blocks.filter((b) => b.type === "h2");

  const go = (target) => (e) => {
    e.preventDefault();
    navigate(`/docs/${target}`);
  };

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <DocsWrapper>
      <TopBar>
        <Brand href="/docs" onClick={go(docsPages[0].slug)}>
          <img src={wordmark} alt="Zephyr" />
          <span>Docs</span>
        </Brand>
        <TopActions>
          <BackLink href="/">← Back to website</BackLink>
          <MenuToggle onClick={() => setMenuOpen((o) => !o)} aria-label="Toggle menu">
            {menuOpen ? <CloseIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
          </MenuToggle>
        </TopActions>
      </TopBar>

      <Layout>
        <Backdrop open={menuOpen} onClick={() => setMenuOpen(false)} />
        <Sidebar open={menuOpen}>
          {docsSections.map((section) => (
            <React.Fragment key={section.group}>
              <GroupTitle>{section.group}</GroupTitle>
              {section.pages.map((p) => (
                <NavItem
                  key={p.slug}
                  href={`/docs/${p.slug}`}
                  active={p.slug === page.slug}
                  onClick={go(p.slug)}
                >
                  {p.title}
                </NavItem>
              ))}
            </React.Fragment>
          ))}
        </Sidebar>

        <Main>
          {page.hero && (
            <Hero>
              <img src={heroImg} alt="Zephyr browser" />
            </Hero>
          )}
          <Breadcrumb>{page.chapter ? `Chapter ${page.chapter}` : page.group}</Breadcrumb>
          <PageTitle>{page.title}</PageTitle>
          {page.subtitle && <Subtitle>{page.subtitle}</Subtitle>}

          <Content>{page.blocks.map(renderBlock)}</Content>

          <PagerRow>
            {prev && (
              <PagerLink href={`/docs/${prev.slug}`} onClick={go(prev.slug)}>
                <small>← Previous</small>
                <span>{prev.title}</span>
              </PagerLink>
            )}
            {next && (
              <PagerLink next href={`/docs/${next.slug}`} onClick={go(next.slug)}>
                <small>Next →</small>
                <span>{next.title}</span>
              </PagerLink>
            )}
          </PagerRow>
          <DocsFooter>ZEPHYR • Privacy. Security. Control.</DocsFooter>
        </Main>

        {headings.length > 0 && (
          <Toc>
            <p>On this page</p>
            {headings.map((h) => (
              <a key={h.text} href={`#${slugify(h.text)}`} onClick={scrollTo(slugify(h.text))}>
                {h.text}
              </a>
            ))}
          </Toc>
        )}
      </Layout>
    </DocsWrapper>
  );
};

export default DocsPage;
